import type { Timestamp } from "@bufbuild/protobuf";
import { createConnectClient } from "../../connect";
import {
  ManagedSecretAccessLevel,
  ManagedSecretService,
  type ManagedSecret,
  type ManagedSecretVersion,
} from "./managedSecretContract";

const managedSecretClient = createConnectClient(ManagedSecretService);
const pageSize = 100;

export { ManagedSecretAccessLevel };

export interface ManagedSecretSummary {
  id: string;
  name: string;
  organizationId: string;
  createTime?: string;
  updateTime?: string;
  latestVersionId: string;
  latestVersionNumber: bigint;
  userAccesses: ManagedSecretUserAccessSummary[];
}

export interface ManagedSecretUserAccessSummary {
  userId: string;
  accessLevel: number;
}

export interface ManagedSecretVersionSummary {
  id: string;
  secretId: string;
  versionNumber: bigint;
  createTime?: string;
}

export interface CreateManagedSecretInput {
  name: string;
  value: string;
  organizationId: string;
}

export interface SetManagedSecretAccessInput {
  secretId: string;
  userId: string;
  accessLevel: number;
}

function formatTimestamp(timestamp?: Timestamp): string | undefined {
  return timestamp?.toDate().toISOString();
}

export function mapManagedSecret(secret: ManagedSecret): ManagedSecretSummary {
  return {
    id: secret.secretId,
    name: secret.name,
    organizationId: secret.organizationId,
    createTime: formatTimestamp(secret.createTime),
    updateTime: formatTimestamp(secret.updateTime),
    latestVersionId: secret.latestVersionId,
    latestVersionNumber: secret.latestVersionNumber,
    userAccesses: secret.userAccesses.map((access) => ({
      userId: access.userId,
      accessLevel: access.accessLevel,
    })),
  };
}

export function mapManagedSecretVersion(
  version: ManagedSecretVersion,
): ManagedSecretVersionSummary {
  return {
    id: version.versionId,
    secretId: version.managedSecretId,
    versionNumber: version.versionNumber,
    createTime: formatTimestamp(version.createTime),
  };
}

export async function listManagedSecrets(
  organizationId: string,
): Promise<ManagedSecretSummary[]> {
  const secrets: ManagedSecretSummary[] = [];
  let pageToken = "";

  do {
    const response = await managedSecretClient.listSecrets({
      organizationId,
      pageParams: { pageSize, pageToken },
    });
    secrets.push(...response.items.map(mapManagedSecret));
    pageToken = response.nextPageToken;
  } while (pageToken);

  return secrets;
}

export async function getManagedSecret(
  secretId: string,
): Promise<ManagedSecretSummary> {
  const response = await managedSecretClient.getSecret({ secretId });
  if (!response.secret) {
    throw new Error("secret was not returned");
  }
  return mapManagedSecret(response.secret);
}

export async function createManagedSecret(
  input: CreateManagedSecretInput,
): Promise<ManagedSecretSummary> {
  const response = await managedSecretClient.createSecret(input);
  if (!response.secret) {
    throw new Error("created secret was not returned");
  }
  return mapManagedSecret(response.secret);
}

export async function listManagedSecretVersions(
  secretId: string,
): Promise<ManagedSecretVersionSummary[]> {
  const versions: ManagedSecretVersionSummary[] = [];
  let pageToken = "";

  do {
    const response = await managedSecretClient.listSecretVersions({
      secretId,
      pageParams: { pageSize, pageToken },
    });
    versions.push(...response.items.map(mapManagedSecretVersion));
    pageToken = response.nextPageToken;
  } while (pageToken);

  return versions.sort((left, right) =>
    left.versionNumber > right.versionNumber
      ? -1
      : left.versionNumber < right.versionNumber
        ? 1
        : 0,
  );
}

export async function getManagedSecretValue(
  secretId: string,
  versionId: string,
): Promise<string> {
  const response = await managedSecretClient.getSecretValue({
    secretId,
    versionId,
  });
  return response.value;
}

export async function setManagedSecretValue(
  secretId: string,
  value: string,
): Promise<ManagedSecretSummary> {
  const response = await managedSecretClient.setSecretValue({
    secretId,
    value,
  });
  if (!response.secret) {
    throw new Error("updated secret was not returned");
  }
  return mapManagedSecret(response.secret);
}

export async function setManagedSecretAccess({
  secretId,
  userId,
  accessLevel,
}: SetManagedSecretAccessInput): Promise<ManagedSecretSummary> {
  const response = await managedSecretClient.setSecretAccess({
    secretId,
    userId,
    accessLevel,
  });
  if (!response.secret) {
    throw new Error("updated secret was not returned");
  }
  return mapManagedSecret(response.secret);
}

export async function removeManagedSecretAccess(
  secretId: string,
  userId: string,
): Promise<ManagedSecretSummary> {
  const response = await managedSecretClient.removeSecretAccess({
    secretId,
    userId,
  });
  if (!response.secret) {
    throw new Error("updated secret was not returned");
  }
  return mapManagedSecret(response.secret);
}

export async function deleteManagedSecret(secretId: string): Promise<void> {
  await managedSecretClient.deleteSecret({ secretId });
}
